import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalFeatureFlag } from '@/lib/featureFlags';

interface PreloadConfig {
  queryKey: any[];
  queryFn: () => Promise<any>;
  delay?: number;
}

/**
 * Week 1 Optimization: Smart Preloading
 * 
 * Performance target: 60% perceived loading reduction
 * Feature flag: ENABLE_SMART_PRELOADING (default: false)
 * 
 * Strategies:
 * - Hover preloading (300ms delay)
 * - Scroll-based prefetch (Intersection Observer)
 * - Background prefetch for common items
 */
export function useSmartPreloading() {
  // ✅ Hardcoded since 2025-10-10 - Always use smart preloading
  const isEnabled = true;
  const queryClient = useQueryClient();
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const preload = useCallback((config: PreloadConfig) => {
    if (!isEnabled) return;

    const { queryKey, queryFn, delay = 300 } = config;
    const key = JSON.stringify(queryKey);

    // Clear existing timer
    const existingTimer = timersRef.current.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new preload timer
    const timer = setTimeout(async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey,
          queryFn,
          staleTime: 5 * 60 * 1000 // 5 minutes
        });
        
        if (import.meta.env.DEV) {
          console.debug(`[SmartPreload] Preloaded: ${key}`);
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn(`[SmartPreload] Failed to preload ${key}:`, error);
        }
      } finally {
        timersRef.current.delete(key);
      }
    }, delay);

    timersRef.current.set(key, timer);
  }, [isEnabled, queryClient]);

  const cancelPreload = useCallback((queryKey: any[]) => {
    const key = JSON.stringify(queryKey);
    const timer = timersRef.current.get(key);
    
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(key);
    }
  }, []);

  const preloadImmediate = useCallback((config: Omit<PreloadConfig, 'delay'>) => {
    preload({ ...config, delay: 0 });
  }, [preload]);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  return {
    preload,
    cancelPreload,
    preloadImmediate,
    cleanup,
    isEnabled
  };
}
